/**
 * Детализация расчёта: смены, расходы, корректировки.
 *
 * Это то, что открывают, когда сумма кажется неправильной. Поэтому здесь
 * видно каждую строку и пометку «время правили» — спор решается взглядом
 * на список, а не перепиской.
 */

import clsx from 'clsx'

import { ADJUSTMENT_LABELS, type Adjustment, type Timesheet } from '@/shared/api/pay'
import { formatDuration, formatTime } from '@/shared/format'
import { Icon } from '@/shared/ui/Icon'
import { Card, Chip, Money, SectionTitle } from '@/shared/ui/primitives'

export function TimesheetDetails({
  timesheet,
  timeZone,
  onVoidAdjustment,
}: {
  timesheet: Timesheet
  timeZone: string
  onVoidAdjustment?: (adjustment: Adjustment) => void
}) {
  return (
    <>
      {timesheet.shifts.length > 0 && (
        <div>
          <SectionTitle>Смены</SectionTitle>
          <Card padded={false} className="px-4 py-1">
            {timesheet.shifts.map((shift) => (
              <div
                key={shift.id}
                className="border-line flex items-baseline gap-3 border-b py-3 last:border-b-0"
              >
                <span className="tabular w-12 shrink-0 text-note font-medium">
                  {shift.date.slice(8, 10)}.{shift.date.slice(5, 7)}
                </span>
                <span className="text-text-muted min-w-0 flex-1 text-caption">
                  {shift.start ? formatTime(shift.start, timeZone) : '—'}–
                  {shift.end ? formatTime(shift.end, timeZone) : '—'}
                  <span className="ml-2">{formatDuration(shift.minutes_total ?? 0)}</span>
                  {shift.edited && (
                    <Chip tone="neutral" className="ml-2">
                      время правили
                    </Chip>
                  )}
                </span>
                <Money value={shift.amount} currency={timesheet.currency} size="sm" />
              </div>
            ))}
          </Card>
        </div>
      )}

      {timesheet.expenses.length > 0 && (
        <div>
          <SectionTitle>Расходы</SectionTitle>
          <Card padded={false} className="px-4 py-1">
            {timesheet.expenses.map((expense) => (
              <div
                key={expense.id}
                className="border-line flex items-center gap-3 border-b py-3 last:border-b-0"
              >
                {expense.receipt_urls[0] ? (
                  <img
                    src={expense.receipt_urls[0]}
                    alt=""
                    loading="lazy"
                    className="size-10 shrink-0 rounded-[var(--radius-thumb)] object-cover"
                  />
                ) : (
                  <span
                    aria-hidden
                    className="bg-warm-soft flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-thumb)]"
                  >
                    <Icon name="exp-receipt" size={21} />
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate text-note">{expense.title}</span>
                <Money value={expense.amount} currency={timesheet.currency} size="sm" />
              </div>
            ))}
          </Card>
        </div>
      )}

      {timesheet.adjustments.length > 0 && (
        <div>
          <SectionTitle>Корректировки</SectionTitle>
          <Card padded={false} className="px-4 py-1">
            {timesheet.adjustments.map((adjustment) => {
              const voided = adjustment.voided_at !== null && adjustment.voided_at !== undefined
              return (
                <div
                  key={adjustment.id}
                  className="border-line border-b py-3 last:border-b-0"
                >
                  <div className="flex items-baseline gap-3">
                    <span
                      className={clsx(
                        'min-w-0 flex-1 truncate text-note',
                        voided && 'text-text-muted line-through',
                      )}
                    >
                      {adjustment.title}
                      {/* тип показываем, только если он что-то добавляет:
                          «Аванс · Аванс» выглядит как ошибка вёрстки */}
                      {ADJUSTMENT_LABELS[adjustment.type] !== adjustment.title && (
                        <span className="text-text-muted ml-2 text-caption">
                          {ADJUSTMENT_LABELS[adjustment.type]}
                        </span>
                      )}
                    </span>
                    <Money
                      value={adjustment.amount}
                      currency={timesheet.currency}
                      size="sm"
                      tone={voided ? 'muted' : Number(adjustment.amount) < 0 ? 'muted' : undefined}
                      className={clsx(voided && 'line-through')}
                    />
                  </div>

                  {voided ? (
                    <p className="text-text-muted mt-1 text-caption">
                      отменено{adjustment.void_reason ? `: ${adjustment.void_reason}` : ''}
                    </p>
                  ) : (
                    onVoidAdjustment && (
                      <button
                        type="button"
                        onClick={() => onVoidAdjustment(adjustment)}
                        className="text-text-muted -my-2 min-h-[44px] py-3 text-caption underline underline-offset-2"
                      >
                        Отменить
                      </button>
                    )
                  )}
                </div>
              )
            })}
          </Card>
        </div>
      )}
    </>
  )
}
